import { Injectable } from '@angular/core';
import { map, Observable, shareReplay } from 'rxjs';
import { MqttConnectorService } from 'src/mqtt/services/mqtt-connector.service';
import { IShadowState } from 'src/mqtt/interfaces/interfaces';
import { actionId, actionType } from 'src/mqtt/actions-list';

// Used when MQTT has not delivered a value, the unit was never configured (HA
// reports 'unknown' once `initial:` is removed from the helper), or we are
// offline. The floor exists so a resident can always tell the light is on.
export const BRIGHTNESS_FLOOR_FALLBACK = 10;
export const BRIGHTNESS_CEILING_FALLBACK = 100;
const NIGHT_LIGHT_ABSOLUTE_FLOOR = 10;
const NIGHT_LIGHT_RANGE = 20;

@Injectable({ providedIn: 'root' })
export class BrightnessBoundsService {
  public readonly floor$: Observable<number>;
  public readonly ceiling$: Observable<number>;
  public readonly nightMin$: Observable<number>;
  public readonly nightMax$: Observable<number>;

  constructor(private mqtt: MqttConnectorService) {
    this.floor$ = this.bound$(actionId.lightBrightnessMin, BRIGHTNESS_FLOOR_FALLBACK);
    this.ceiling$ = this.bound$(actionId.lightBrightnessMax, BRIGHTNESS_CEILING_FALLBACK);

    this.nightMin$ = this.floor$.pipe(
      map((floor) => Math.max(floor, NIGHT_LIGHT_ABSOLUTE_FLOOR)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.nightMax$ = this.nightMin$.pipe(
      map((nightMin) => Math.min(nightMin + NIGHT_LIGHT_RANGE, 100)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  private bound$(id: string, fallback: number): Observable<number> {
    // No startWith here: the underlying source (MqttMessageStorageService's
    // items$) is a BehaviorSubject, so every subscriber already gets a
    // synchronous current-or-undefined value. Prepending one with startWith
    // would fire an extra synchronous 'undefined' emission ahead of that real
    // one — harmless to an ordinary subscribe(), but it races anything that
    // reads only the first value (e.g. firstValueFrom), which would then
    // observe the fallback and never see the real value at all.
    return this.mqtt.getMessageListener(`${actionType.inputText}.${id}`).pipe(
      map((message: IShadowState | undefined) => this.parse(message, fallback)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  private parse(message: IShadowState | undefined, fallback: number): number {
    const raw = message?.reported?.state;
    if (raw === undefined || raw === null || raw === '') return fallback;
    const n = Number(raw);
    // Number('unknown') is NaN, and typeof NaN === 'number' — so this check
    // cannot be a typeof test.
    if (!Number.isFinite(n)) return fallback;
    const rounded = Math.round(n);
    // 0 is treated as unset, never as a real bound: 0 means "off", so it is not a
    // meaningful floor, and every fielded unit carries a stale shadow for these
    // helpers reporting exactly "0" (last written 2026-04-29, from the helper's
    // since-removed `initial: 0`). Returning `fallback` here reuses the caller's
    // BRIGHTNESS_FLOOR_FALLBACK / BRIGHTNESS_CEILING_FALLBACK rather than
    // restating the number. Mirrors the same rule at vOS's six read sites.
    return rounded > 0 && rounded <= 100 ? rounded : fallback;
  }
}
